import React from 'react';
import clsx from 'clsx';
import {type RowData} from '@tanstack/react-table';
import {type ColumnPinningState} from '@tanstack/table-core';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {TableUtilityCellType} from '../constants';
import {
	getTableCellUtilityClass,
	TABLE_CELL_CLASS_NAME,
	tableCellClasses,
	type TableCellClasses,
} from './tableCellClasses';

const TABLE_UTILITY_CELL_TYPES = Object.values(TableUtilityCellType);

type TableCellOwnerState = WithClasses<
	TableCellClasses,
	{
		pinnedLeft?: boolean;
		pinnedRight?: boolean;
		lastPinnedLeft?: boolean;
		firstPinnedRight?: boolean;
		verticalAlign?: 'baseline' | 'center';

		isActionCell?: boolean;
		isDataCell?: boolean;
		isDragHandleCell?: boolean;
		isSelectionCell?: boolean;
		isThumbnailCell?: boolean;
	}
>;

const useUtilityClasses = (ownerState: TableCellOwnerState) => {
	const {
		classes,
		pinnedLeft,
		pinnedRight,
		lastPinnedLeft,
		firstPinnedRight,
		verticalAlign,
		isActionCell,
		isDataCell,
		isDragHandleCell,
		isSelectionCell,
		isThumbnailCell,
	} = ownerState;
	const slots = {
		root: [
			'root',
			pinnedLeft && 'pinnedLeft',
			pinnedRight && 'pinnedRight',
			lastPinnedLeft && 'lastPinnedLeft',
			firstPinnedRight && 'firstPinnedRight',
			verticalAlign === 'baseline' && 'alignBaseline',
			isActionCell && 'isActionCell',
			isDataCell && 'isDataCell',
			isDragHandleCell && 'isDragHandleCell',
			isSelectionCell && 'isSelectionCell',
			isThumbnailCell && 'isThumbnailCell',
			!isDataCell && 'isUtilityCell',
		],
	};
	return composeClasses(slots, getTableCellUtilityClass, classes);
};

const TableCellRoot = styled('div', {
	name: TABLE_CELL_CLASS_NAME,
	slot: 'Root',
})(({theme, ownerState}) => ({
	backgroundColor: theme.palette.background.paper,
	borderRight: `1px solid transparent`,
	borderLeft: `1px solid transparent`,
	display: 'flex',
	alignItems: ownerState?.verticalAlign === 'center' ? 'center' : undefined,
	flex: '0 0 auto',
	minHeight: '100%',
	padding: theme.spacing(1),
	zIndex: 1,
	[`&.${tableCellClasses.pinnedLeft}`]: {
		...theme.mixins.positionSticky,
		left: 0,
		zIndex: 4,
	},
	[`&.${tableCellClasses.pinnedRight}`]: {
		...theme.mixins.positionSticky,
		right: 0,
		zIndex: 3,
	},
	...(ownerState.isDataCell && {
		paddingLeft: 16,
		paddingRight: 16,
	}),
}));

export interface TableCellProps<TData extends RowData>
	extends WithClasses<
		TableCellClasses,
		React.PropsWithChildren<
			React.DetailedHTMLProps<
				React.HTMLAttributes<HTMLDivElement>,
				HTMLDivElement
			>
		>
	> {
	disabled?: boolean;
	columnId: string;
	index: number;
	isDataCell?: boolean;
	leftLeafColumnWidths?: number[];
	pinnedColumns?: ColumnPinningState;
	rightLeafColumnWidths?: number[];
	sortType?: 'numeric' | 'alphabetic';
	verticalAlign?: 'baseline' | 'center';
	horizontalAlign?: 'left' | 'center' | 'right';
	width: number;
}

export const TableCellPrimitive = <TData extends RowData>({
	classes: _classes,
	className,
	columnId,
	index,
	isDataCell,
	leftLeafColumnWidths,
	pinnedColumns,
	rightLeafColumnWidths,
	sortType,
	horizontalAlign,
	verticalAlign = 'center',
	width,
	...props
}: TableCellProps<TData>) => {
	const pinnedLeftIndex = pinnedColumns?.left?.indexOf(columnId) ?? -1;
	const pinnedRightIndex = pinnedColumns?.right?.indexOf(columnId) ?? -1;

	const pinnedLeft = pinnedLeftIndex > -1;
	const pinnedRight = pinnedRightIndex > -1;

	const lastPinned =
		(pinnedLeft && pinnedColumns!.left!.length - 1 === pinnedLeftIndex) ||
		(pinnedRight && pinnedRightIndex === 0);

	const pinFromLeft = pinnedLeft
		? leftLeafColumnWidths
				?.slice(0, pinnedLeftIndex)
				.reduce((acc, width) => acc + width, 0)
		: undefined;

	const pinFromRight = pinnedRight
		? rightLeafColumnWidths
				?.slice(pinnedRightIndex + 1)
				.reduce((acc, width) => acc + width, 0)
		: undefined;

	if (isDataCell == null)
		isDataCell = !TABLE_UTILITY_CELL_TYPES.includes(
			columnId as typeof TABLE_UTILITY_CELL_TYPES[number],
		);

	const ownerState: TableCellOwnerState = {
		classes: _classes,
		className,
		pinnedLeft,
		pinnedRight,
		lastPinnedLeft: pinnedLeft && lastPinned,
		firstPinnedRight: pinnedRight && lastPinned,
		isActionCell: columnId === TableUtilityCellType.ACTIONS,
		isDragHandleCell: columnId === TableUtilityCellType.DRAG_HANDLE,
		isSelectionCell: columnId === TableUtilityCellType.SELECTION,
		isThumbnailCell: columnId === TableUtilityCellType.THUMBNAIL,
		isDataCell,
		verticalAlign,
	};

	const classes = useUtilityClasses(ownerState);

	return (
		<TableCellRoot
			className={clsx(classes.root, className)}
			role="cell"
			{...props}
			ownerState={{...ownerState, ...(props as any).ownerState}}
			style={{
				left: pinFromLeft,
				right: pinFromRight,
				width,
				// TODO: c'mon - i did this quickly and never came back to clean up
				// re-assess the approcach altogether
				justifyContent: horizontalAlign
					? horizontalAlign === 'center'
						? 'center'
						: horizontalAlign === 'right'
						? 'flex-end'
						: 'flex-start'
					: sortType === 'numeric'
					? 'flex-end'
					: 'flex-start',
				...props.style,
			}}
		/>
	);
};

export const TableCell = React.memo(TableCellPrimitive) as <
	TData extends RowData,
>(
	props: TableCellProps<TData>,
) => JSX.Element;

export default TableCell;
